import { useState, useMemo } from 'react';
import {
  Table, Button, Tag, Space, Modal, Form, Input, InputNumber,
  Switch, App as AntdApp, Popconfirm, Drawer, Select, Divider,
  Empty, Spin, ColorPicker, Badge,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Plus, Edit2, Trash2, Shield, UserPlus, X as XIcon, FolderOpen,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { documentService, type DocumentCategory } from '@/services/documentService';
import { userService } from '@/services/userService';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';

const ROLE_OPTIONS = [
  { value: 'STAFF', label: 'Nhân viên' },
  { value: 'DEPT_HEAD', label: 'Trưởng/Phó khoa phòng' },
  { value: 'BOD', label: 'Ban Giám đốc' },
  { value: 'SYS_ADMIN', label: 'Quản trị viên' },
];

const PERMISSION_OPTIONS = [
  { value: 'VIEW', label: 'Xem', color: 'blue' },
  { value: 'UPLOAD', label: 'Tạo + Xem', color: 'cyan' },
  { value: 'MANAGE', label: 'Toàn quyền', color: 'purple' },
];

export function AdminCategoriesPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { message, modal } = AntdApp.useApp();
  const qc = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentCategory | null>(null);
  const [permOpen, setPermOpen] = useState(false);
  const [permCategory, setPermCategory] = useState<DocumentCategory | null>(null);
  const [form] = Form.useForm();
  const [grantForm] = Form.useForm();

  // Redirect nếu không phải ADMIN
  const isAdmin = user?.roles?.includes('SYS_ADMIN');
  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Shield size={48} style={{ color: '#d4d9df' }} />
        <h3 style={{ marginTop: 16 }}>Không có quyền truy cập</h3>
        <p style={{ color: '#7d8a98' }}>Chỉ quản trị viên mới được quản lý danh mục tài liệu.</p>
        <Button onClick={() => navigate('/dashboard')}>Về trang chủ</Button>
      </div>
    );
  }

  // Query categories - backend sẽ trả hết vì admin
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['doc-categories-admin'],
    queryFn: () => documentService.listCategories(),
  });

  // Query permissions của category đang xem
  const { data: permissions = [], refetch: refetchPerms } = useQuery({
    queryKey: ['doc-category-permissions', permCategory?.id],
    queryFn: () => documentService.getCategoryPermissions(permCategory!.id),
    enabled: !!permCategory,
  });

  // Query users cho dropdown grant permission
  const { data: usersResp } = useQuery({
    queryKey: ['users-for-perm'],
    queryFn: () => userService.list({ pageSize: 1000 }),
    enabled: permOpen,
  });

  const createMutation = useMutation({
    mutationFn: (payload: any) => documentService.createCategory(payload),
    onSuccess: () => {
      message.success('Đã tạo danh mục');
      setEditOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['doc-categories-admin'] });
      qc.invalidateQueries({ queryKey: ['doc-categories'] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Tạo thất bại'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      documentService.updateCategory(id, payload),
    onSuccess: () => {
      message.success('Đã cập nhật danh mục');
      setEditOpen(false);
      setEditing(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['doc-categories-admin'] });
      qc.invalidateQueries({ queryKey: ['doc-categories'] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Cập nhật thất bại'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentService.deleteCategory(id),
    onSuccess: () => {
      message.success('Đã xóa danh mục');
      qc.invalidateQueries({ queryKey: ['doc-categories-admin'] });
      qc.invalidateQueries({ queryKey: ['doc-categories'] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Xóa thất bại'),
  });

  const grantMutation = useMutation({
    mutationFn: (payload: any) =>
      documentService.grantCategoryPermission(permCategory!.id, payload),
    onSuccess: () => {
      message.success('Đã cấp quyền');
      grantForm.resetFields();
      refetchPerms();
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Cấp quyền thất bại'),
  });

  const revokeMutation = useMutation({
    mutationFn: (permissionId: string) =>
      documentService.revokeCategoryPermission(permissionId),
    onSuccess: () => {
      message.success('Đã thu hồi quyền');
      refetchPerms();
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Thu hồi thất bại'),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      sortOrder: (categories.length + 1) * 10,
      color: '#6699CC',
    });
    setEditOpen(true);
  };

  const openEdit = (cat: DocumentCategory) => {
    setEditing(cat);
    form.setFieldsValue({
      code: cat.code,
      name: cat.name,
      description: cat.description,
      color: cat.color ?? '#6699CC',
      prefix: cat.prefix,
      retentionMonths: cat.retentionMonths,
      sortOrder: cat.sortOrder,
      isActive: cat.isActive,
      parentId: cat.parentId ?? undefined,
    });
    setEditOpen(true);
  };

  // Convert flat list -> tree (nested children) cho hiển thị Antd Table tree mode
  const treeData = useMemo(() => {
    const byId = new Map<string, DocumentCategory & { children?: any[] }>();
    categories.forEach(c => byId.set(c.id, { ...c }));
    const roots: any[] = [];
    byId.forEach(c => {
      if (c.parentId && byId.has(c.parentId)) {
        const parent = byId.get(c.parentId)!;
        parent.children = parent.children ?? [];
        parent.children.push(c);
      } else {
        roots.push(c);
      }
    });
    return roots;
  }, [categories]);

  // Tính danh sách option cho parent select — loại bỏ chính nó + các con của nó (tránh circular)
  const parentOptions = useMemo(() => {
    if (!editing) return categories.filter(c => !c.parentId || true);  // create: cho phép tất cả
    // Edit: loại self + descendants
    const excluded = new Set<string>([editing.id]);
    const queue = [editing.id];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      categories.filter(c => c.parentId === cur).forEach(c => {
        excluded.add(c.id);
        queue.push(c.id);
      });
    }
    return categories.filter(c => !excluded.has(c.id));
  }, [categories, editing]);

  const submitForm = async () => {
    const values = await form.validateFields();
    // Normalize color từ ColorPicker (có thể là object {toHexString} hoặc string)
    if (values.color && typeof values.color === 'object' && typeof values.color.toHexString === 'function') {
      values.color = values.color.toHexString();
    }
    if (editing) {
      // Update - không gửi code + prefix (không cho đổi)
      const { code, prefix, ...payload } = values;
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(values);
    }
  };

  const columns: ColumnsType<DocumentCategory> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 120,
      render: (code, r) => (
        <Space>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: r.color ?? '#6699CC',
          }} />
          <b className="mono">{code}</b>
        </Space>
      ),
    },
    {
      title: 'Tên danh mục',
      dataIndex: 'name',
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {r.description && (
            <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 2 }}>
              {r.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Prefix',
      dataIndex: 'prefix',
      width: 90,
      render: (p) => <Tag className="mono">{p}</Tag>,
    },
    {
      title: 'Số tài liệu',
      dataIndex: 'documentCount',
      width: 110,
      align: 'center' as const,
      render: (c: number) => <Badge count={c ?? 0} showZero color="#6699CC" />,
    },
    {
      title: 'Hạn lưu (tháng)',
      dataIndex: 'retentionMonths',
      width: 130,
      align: 'center' as const,
      render: (m?: number) => m ? <span>{m}</span> : <span style={{ color: '#adb6c0' }}>—</span>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 110,
      render: (active: boolean) => active
        ? <Tag color="green">Đang dùng</Tag>
        : <Tag color="default">Tạm ngưng</Tag>,
    },
    {
      title: 'Thao tác',
      width: 210,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<Shield size={12} />}
            onClick={() => {
              setPermCategory(r);
              setPermOpen(true);
            }}>
            Phân quyền
          </Button>
          <Button size="small" icon={<Edit2 size={12} />} onClick={() => openEdit(r)} />
          <Popconfirm
            title="Xóa danh mục này?"
            description={r.documentCount && r.documentCount > 0
              ? `⚠ Không thể xóa - đang có ${r.documentCount} tài liệu`
              : 'Không thể hoàn tác'}
            okText="Xóa"
            okButtonProps={{ danger: true, disabled: !!r.documentCount && r.documentCount > 0 }}
            cancelText="Hủy"
            onConfirm={() => {
              if (r.documentCount && r.documentCount > 0) {
                message.warning('Không thể xóa: còn tài liệu trong danh mục này');
                return;
              }
              deleteMutation.mutate(r.id);
            }}
          >
            <Button size="small" danger icon={<Trash2 size={12} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Quản lý danh mục tài liệu"
        breadcrumb="Quản trị / Danh mục tài liệu"
        extra={
          <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
            Thêm danh mục
          </Button>
        }
      />

      <Table<DocumentCategory>
        rowKey="id"
        columns={columns}
        dataSource={treeData as DocumentCategory[]}
        loading={isLoading}
        pagination={false}
        defaultExpandAllRows
        indentSize={20}
        locale={{ emptyText: <Empty description="Chưa có danh mục nào" /> }}
      />

      {/* ================== MODAL TẠO/SỬA ================== */}
      <Modal
        title={editing ? `Sửa danh mục: ${editing.name}` : 'Thêm danh mục mới'}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={submitForm}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okText={editing ? 'Lưu' : 'Tạo'}
        cancelText="Hủy"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Mã danh mục (code)" name="code"
            rules={[{ required: true, message: 'Bắt buộc' }]}
            extra={editing ? 'Không thể đổi mã sau khi tạo' : 'VD: HDCV, QT, CV, BC... Viết hoa, không khoảng trắng'}>
            <Input disabled={!!editing} placeholder="HDCV" style={{ textTransform: 'uppercase' }} />
          </Form.Item>

          <Form.Item label="Tên danh mục" name="name" rules={[{ required: true, message: 'Bắt buộc' }]}>
            <Input placeholder="Hướng dẫn công việc" />
          </Form.Item>

          <Form.Item label="Mô tả" name="description">
            <Input.TextArea rows={2} placeholder="Mô tả ngắn về danh mục" />
          </Form.Item>

          <Form.Item label="Thư mục cha" name="parentId"
            extra="Để trống = thư mục gốc. Chọn để tạo thư mục con (lồng nhau)">
            <Select
              allowClear
              placeholder="(Thư mục gốc — không có cha)"
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={parentOptions.map(c => ({
                value: c.id,
                label: c.name + (c.code ? ` (${c.code})` : ''),
              }))}
            />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item label="Prefix (mã tài liệu)" name="prefix"
              rules={[{ required: true, message: 'Bắt buộc' }]}
              extra={editing ? 'Không thể đổi' : 'VD: HDCV, QT - tài liệu sẽ có mã HDCV-2026-01-001'}>
              <Input disabled={!!editing} placeholder="HDCV" style={{ textTransform: 'uppercase' }} />
            </Form.Item>

            <Form.Item label="Thứ tự" name="sortOrder"
              rules={[{ required: true }]}>
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item label="Hạn lưu (tháng)" name="retentionMonths"
              extra="Để trống = lưu vĩnh viễn">
              <InputNumber min={1} max={1200} style={{ width: '100%' }}
                placeholder="VD: 60 = 5 năm" />
            </Form.Item>

            <Form.Item label="Màu sắc" name="color">
              <ColorPicker showText format="hex" />
            </Form.Item>
          </div>

          {editing && (
            <Form.Item label="Trạng thái" name="isActive" valuePropName="checked">
              <Switch checkedChildren="Đang dùng" unCheckedChildren="Tạm ngưng" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* ================== DRAWER PHÂN QUYỀN ================== */}
      <Drawer
        title={
          <div>
            <div style={{ fontSize: 14 }}>Phân quyền danh mục</div>
            {permCategory && (
              <div style={{ fontSize: 12, color: '#7d8a98', fontWeight: 400, marginTop: 2 }}>
                <FolderOpen size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                {permCategory.name}
              </div>
            )}
          </div>
        }
        open={permOpen}
        onClose={() => {
          setPermOpen(false);
          setPermCategory(null);
          grantForm.resetFields();
        }}
        width={540}
        extra={
          <Button size="small" onClick={() => {
            setPermOpen(false);
            setPermCategory(null);
            grantForm.resetFields();
          }}>Đóng</Button>
        }
      >
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ marginTop: 0, fontSize: 13 }}>Cấp quyền mới</h4>
          <Form
            form={grantForm}
            layout="vertical"
            onFinish={(v) => {
              if (!v.userId && !v.role) {
                message.warning('Chọn 1 user hoặc 1 role');
                return;
              }
              if (v.userId && v.role) {
                message.warning('Chỉ chọn 1 trong 2 (user hoặc role)');
                return;
              }
              grantMutation.mutate({
                userId: v.userId || undefined,
                role: v.role || undefined,
                permission: v.permission,
              });
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Form.Item label="Cấp cho User" name="userId">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Chọn người..."
                  options={(usersResp?.items ?? []).map((u: any) => ({
                    value: u.id,
                    label: `${u.fullName} (${u.department?.name ?? ''})`,
                  }))}
                  onChange={(v) => {
                    if (v) grantForm.setFieldValue('role', undefined);
                  }}
                />
              </Form.Item>
              <Form.Item label="HOẶC Role" name="role">
                <Select
                  allowClear
                  placeholder="Chọn vai trò..."
                  options={ROLE_OPTIONS}
                  onChange={(v) => {
                    if (v) grantForm.setFieldValue('userId', undefined);
                  }}
                />
              </Form.Item>
            </div>
            <Form.Item label="Quyền" name="permission" initialValue="VIEW"
              rules={[{ required: true }]}>
              <Select options={PERMISSION_OPTIONS} />
            </Form.Item>
            <Button type="primary" htmlType="submit" icon={<UserPlus size={13} />}
              loading={grantMutation.isPending} block>
              Cấp quyền
            </Button>
          </Form>
        </div>

        <Divider />

        <h4 style={{ fontSize: 13, marginTop: 0 }}>Đang có quyền ({permissions.length})</h4>
        {permissions.length === 0 ? (
          <Empty description="Chưa cấp quyền cho ai" style={{ padding: 24 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {permissions.map((p: any) => {
              const permOption = PERMISSION_OPTIONS.find(o => o.value === p.permission);
              return (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: 10, border: '1px solid #eceef1', borderRadius: 6,
                }}>
                  <div style={{ flex: 1 }}>
                    {p.user ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{p.user.fullName}</div>
                        <div style={{ fontSize: 11, color: '#7d8a98' }}>
                          {p.user.position ?? 'User'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>
                          <Shield size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                          Role: {ROLE_OPTIONS.find(r => r.value === p.role)?.label ?? p.role}
                        </div>
                        <div style={{ fontSize: 11, color: '#7d8a98' }}>
                          Áp dụng cho tất cả người có vai trò này
                        </div>
                      </>
                    )}
                  </div>
                  <Tag color={permOption?.color ?? 'default'}>{permOption?.label ?? p.permission}</Tag>
                  <Popconfirm
                    title="Thu hồi quyền này?"
                    okText="Thu hồi"
                    okButtonProps={{ danger: true }}
                    cancelText="Hủy"
                    onConfirm={() => revokeMutation.mutate(p.id)}
                  >
                    <Button size="small" danger type="text" icon={<XIcon size={14} />} />
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </>
  );
}
